import type { StudioThemeId } from 'fym-shared/src/music24/studioThemes';
import { SerializedKitAsset } from '../../../../redux/api/fymAPI.types';
import { PromoRenderAspect, SerializedReel } from '../types';
import { motionLoopUrl } from '../studio/fullVideoProgress';

/** One ready file that the Composer can post: a kit asset or an AI reel ("Post it"). */
export interface LibraryFile {
  key: string;
  themeId: StudioThemeId;
  /** The relative `files/<uuid>/download` url. */
  fileUrl: string;
  posterUrl: string | null;
  /**
   * The loop the card plays on hover: the MP4 of the motion artwork (an old ProRes row: its H.264
   * preview); else null.
   */
  previewUrl: string | null;
  isVideo: boolean;
  /** The FileModel id. "Post it" needs it. */
  fileId: number | null;
  width: number | null;
  height: number | null;
  durationSec: number | null;
  madeAt: string;
  /** The row the file comes from: the asset viewer reads its facts and its share link. */
  source?: { kind: 'kitAsset'; asset: SerializedKitAsset } | { kind: 'reel'; reel: SerializedReel };
}

/** The Library file of a ready, current kit asset, or null for any other asset. */
export function kitAssetLibraryFile(asset: SerializedKitAsset): LibraryFile | null {
  if (asset.status !== 'ready' || asset.fileUrl === null || asset.supersededAt !== null) {
    return null;
  }
  return {
    key: `asset-${asset.uuid}`,
    themeId: asset.themeId,
    fileUrl: asset.fileUrl,
    posterUrl: asset.posterUrl,
    previewUrl: motionLoopUrl(asset),
    isVideo: asset.durationSec !== null,
    fileId: asset.fileId,
    width: asset.width,
    height: asset.height,
    durationSec: asset.durationSec,
    madeAt: asset.renderedAt ?? asset.createdAt,
    source: { kind: 'kitAsset', asset },
  };
}

/** The Library file of a ready or approved reel with a video, else null. */
export function reelLibraryFile(reel: SerializedReel): LibraryFile | null {
  if ((reel.status !== 'ready' && reel.status !== 'approved') || reel.video === null) {
    return null;
  }
  return {
    key: `reel-${reel.uuid}`,
    themeId: 'aiReel',
    fileUrl: reel.video.url,
    posterUrl: reel.video.posterUrl,
    previewUrl: null,
    isVideo: true,
    fileId: reel.video.fileId,
    width: null,
    height: null,
    durationSec: reel.durationSec,
    madeAt: reel.createdAt,
    source: { kind: 'reel', reel },
  };
}

/**
 * The Composer shape of a file, or null when the Composer cannot post it. The Composer posts a
 * video in 9:16 or 1:1 only, and never the motion artwork (it is for the distributor, not a post).
 * An AI reel is always 9:16.
 */
export function libraryPostAspect(file: LibraryFile): PromoRenderAspect | null {
  if (!file.isVideo || file.themeId === 'motionArtwork' || file.fileId === null || file.durationSec === null) {
    return null;
  }
  if (file.width === null || file.height === null) {
    return file.themeId === 'aiReel' ? '9:16' : null;
  }
  if (file.width === file.height) {
    return '1:1';
  }
  return file.width * 16 === file.height * 9 ? '9:16' : null;
}

/**
 * True when "Post it" shows for a kit asset: it is ready and current, it has a file id, and the
 * Composer posts its shape (`libraryPostAspect`).
 */
export function canPostKitAsset(asset: SerializedKitAsset): boolean {
  const file = kitAssetLibraryFile(asset);
  return file !== null && libraryPostAspect(file) !== null;
}
